<template>
  <div>
    <v-card class="elevation-2">
    <v-card-title class="d-flex justify-space-between align-center">
      <span>Mis Inscripciones</span>
      <v-chip color="primary" small text-color="white">
        {{ registrations.length }}
      </v-chip>
    </v-card-title>
    <v-divider></v-divider>

    <v-card-text>
      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

      <div v-if="!loading && registrations.length === 0" class="text-center py-8">
        <v-icon large color="grey lighten-1" class="mb-3">mdi-clipboard-outline</v-icon>
        <p class="grey--text">No tienes inscripciones en torneos</p>
        <p class="grey--text text-caption">Explora torneos disponibles para inscribirte</p>
      </div>

      <v-data-table
        v-else
        :headers="headers"
        :items="enrichedRegistrations"
        :loading="loading"
        hide-default-footer
        class="elevation-0"
      >
        <template #item.tournament_name="{ item }">
          <div>
            <div class="font-weight-bold">{{ item.tournament_name }}</div>
            <div class="text-caption grey--text">{{ formatDate(item.tournament_date) }}</div>
          </div>
        </template>

        <template #item.player_handicap="{ item }">
          <v-chip small color="primary" text-color="white">
            {{ item.player_handicap }}
          </v-chip>
        </template>

        <template #item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" small text-color="white">
            {{ getRegistrationStatusLabel(item.status) }}
          </v-chip>
        </template>

        <template #item.registered_at="{ item }">
          {{ formatDateTime(item.registered_at) }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex gap-2">
            <v-btn
              small
              icon
              color="primary"
              @click="openDetail(item)"
              title="Ver detalles"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              v-if="item.status === 2"
              small
              icon
              color="error"
              @click="$emit('cancel', item.id)"
              title="Cancelar inscripción"
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>

  <!-- Modal: Detalles de Inscripción -->
  <v-dialog v-model="showDetail" max-width="600px" persistent>
    <v-card v-if="selectedRegistration" class="elevation-2">
      <v-card-title class="primary--text">
        Detalles de Inscripción
        <v-spacer></v-spacer>
        <v-btn icon small @click="showDetail = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <!-- Torneo -->
        <v-row class="mb-4">
          <v-col cols="12">
            <div class="text-caption font-weight-bold mb-2">TORNEO</div>
            <div class="text-body2">{{ getTournamentName(selectedRegistration.tournament) }}</div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Jugador -->
        <v-row class="mb-4">
          <v-col cols="12">
            <div class="text-caption font-weight-bold mb-2">JUGADOR</div>
            <div class="text-body2">{{ selectedRegistration.player_display }}</div>
            <div class="text-caption grey--text">Handicap: {{ selectedRegistration.player_handicap }}</div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Estado -->
        <v-row class="mb-4">
          <v-col cols="6">
            <div class="text-caption font-weight-bold mb-2">ESTADO</div>
            <v-chip
              :color="getStatusColor(selectedRegistration.status)"
              small
              text-color="white"
            >
              {{ getRegistrationStatusLabel(selectedRegistration.status) }}
            </v-chip>
          </v-col>
          <v-col cols="6">
            <div class="text-caption font-weight-bold mb-2">MÉTODO INSCRIPCIÓN</div>
            <div class="text-caption">{{ selectedRegistration.registration_method_display }}</div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Fechas -->
        <v-row class="mb-4">
          <v-col cols="6">
            <div class="text-caption font-weight-bold mb-2">INSCRITO EL</div>
            <div class="text-caption">{{ formatDateTime(selectedRegistration.registered_at) }}</div>
          </v-col>
          <v-col cols="6">
            <div class="text-caption font-weight-bold mb-2">INVITACIÓN ENVIADA</div>
            <div class="text-caption">{{ formatDateTime(selectedRegistration.invitation_sent_at) }}</div>
          </v-col>
        </v-row>

        <!-- Notas si existen -->
        <v-row v-if="selectedRegistration.notes" class="mb-4">
          <v-col cols="12">
            <v-divider class="my-4"></v-divider>
            <div class="text-caption font-weight-bold mb-2">NOTAS</div>
            <div class="text-caption">{{ selectedRegistration.notes }}</div>
          </v-col>
        </v-row>

        <!-- Inscrito por -->
        <v-row v-if="selectedRegistration.registered_by_display" class="mb-4">
          <v-col cols="12">
            <v-divider class="my-4"></v-divider>
            <div class="text-caption font-weight-bold mb-2">INSCRITO POR</div>
            <div class="text-caption">{{ selectedRegistration.registered_by_display }}</div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="showDetail = false">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'MyTournaments',
  props: {
    registrations: {
      type: Array,
      required: true
    },
    tournaments: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      headers: [
        { text: 'Torneo', value: 'tournament_name', sortable: false },
        { text: 'Jugador', value: 'player_display', sortable: false },
        { text: 'Handicap', value: 'player_handicap', sortable: false, width: '100px' },
        { text: 'Estado', value: 'status', sortable: false, width: '120px' },
        { text: 'Fecha Inscripción', value: 'registered_at', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false, width: '100px' }
      ],
      showDetail: false,
      selectedRegistration: null
    }
  },
  computed: {
    enrichedRegistrations () {
      return this.registrations.map(reg => {
        const tournament = this.tournaments.find(t => t.id === reg.tournament)
        return {
          ...reg,
          tournament_name: tournament?.name || tournament?.nombre || 'Desconocido',
          tournament_date: tournament?.date || tournament?.fecha || null
        }
      })
    }
  },
  methods: {
    getRegistrationStatusLabel (status) {
      const labels = {
        1: 'Pendiente',
        2: 'Confirmado',
        3: 'Cancelado',
        4: 'No asistió'
      }
      return labels[status] || 'Desconocido'
    },
    getStatusColor (status) {
      const colors = {
        1: 'warning',
        2: 'success',
        3: 'grey',
        4: 'error'
      }
      return colors[status] || 'grey'
    },
    formatDate (date) {
      return new Date(date).toLocaleDateString('es-ES', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },
    formatDateTime (date) {
      return new Date(date).toLocaleDateString('es-ES', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    getTournamentName (tournamentId) {
      const tournament = this.tournaments.find(t => t.id === tournamentId)
      return tournament?.name || tournament?.nombre || 'Desconocido'
    },
    openDetail (registration) {
      this.selectedRegistration = registration
      this.showDetail = true
    }
  }
}
</script>

<style scoped>
::v-deep .v-data-table {
  background: transparent;
}

::v-deep .v-data-table__wrapper {
  padding: 0;
}
</style>
